import type { ReactNode } from 'react';
import type { MemberDisplayListItem, ScheduleListItem } from '../appService';
import type { WorkspaceBootstrapContext } from './appTypes';
import { useWorkspaceRouteAdapter } from './context';
import { useMemberDisplayListQuery, useScheduleHomeUpcomingQuery, useUnreadNotificationStateQuery } from './data';
import { WorkspaceResourcesTabContent } from './resources';
import {
  CompactEmptyRow,
  ModuleEmptyState,
  OwnJoinRequests,
  RecoverableReadError,
  SectionLoading,
  SvgIcon,
  WorkspaceModuleTopBar,
  formatMemberGroupNames,
  getChoirAvatarLabel
} from './ui';

export function WorkspaceHomeTab({
  appContext,
  renderScheduleRows
}: {
  readonly appContext: WorkspaceBootstrapContext;
  readonly renderScheduleRows: (schedules: readonly ScheduleListItem[]) => ReactNode;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const homeSchedule = useScheduleHomeUpcomingQuery();

  return (
    <>
      <ChoirContextBar choirName={appContext.currentChoir.name} />
      <section className="home-workbench">
        <div className="home-section-heading">
          <h2>近期日程</h2>
          <button
            type="button"
            className="text-button"
            data-action="switch-tab-schedule"
            onClick={() => routeAdapter.replaceMainTab('schedule')}
          >
            查看日程
          </button>
        </div>
        {homeSchedule.status === 'pending' ? <SectionLoading body="正在读取近期日程。" /> : null}
        {homeSchedule.status === 'error' ? (
          <RecoverableReadError
            error={homeSchedule.error}
            retryAction="retry-home-upcoming-schedule"
            onRetry={() => void homeSchedule.refetch()}
          />
        ) : null}
        {homeSchedule.status === 'success' && homeSchedule.data ? (
          <section className="home-schedule-list">{renderScheduleRows([homeSchedule.data])}</section>
        ) : null}
        {homeSchedule.status === 'success' && !homeSchedule.data ? <CompactEmptyRow body="暂无近期日程" /> : null}
      </section>
      <OwnJoinRequests requests={appContext.ownJoinRequests} />
    </>
  );
}

function ChoirContextBar({ choirName }: { readonly choirName: string }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const unreadNotifications = useUnreadNotificationStateQuery();
  const hasUnreadNotifications = unreadNotifications.status === 'success' && unreadNotifications.data.hasUnread;

  return (
    <header className="choir-context-bar">
      <button
        type="button"
        className="choir-context-button context-button"
        data-action="open-choir-management"
        aria-label={`进入合唱团管理：${choirName}`}
        onClick={routeAdapter.openChoirManagement}
      >
        <span className="choir-avatar" aria-hidden="true">
          {getChoirAvatarLabel(choirName)}
        </span>
        <span className="choir-context-name page-title" tabIndex={-1}>
          {choirName}
        </span>
        <span className="choir-context-chevron" aria-hidden="true">
          ›
        </span>
      </button>
      <button
        type="button"
        className="notification-button"
        data-action="open-notifications"
        aria-label="通知"
        aria-describedby="notification-bell-status"
        onClick={routeAdapter.openNotifications}
      >
        <SvgIcon name="bell" className="notification-icon" />
        {hasUnreadNotifications ? <span className="notification-unread-dot" aria-hidden="true" /> : null}
        <span id="notification-bell-status" className="sr-only">
          {hasUnreadNotifications ? '有未读通知' : '没有未读通知'}
        </span>
      </button>
    </header>
  );
}

export function WorkspaceResourcesTab(): ReactNode {
  return <WorkspaceResourcesTabContent />;
}

export function WorkspaceMembersTab(): ReactNode {
  const members = useMemberDisplayListQuery();

  return (
    <>
      <WorkspaceModuleTopBar title="成员" />
      <MemberDisplayListContent members={members} />
    </>
  );
}

function MemberDisplayListContent({
  members
}: {
  readonly members: ReturnType<typeof useMemberDisplayListQuery>;
}): ReactNode {
  if (members.status === 'pending') {
    return <SectionLoading body="正在读取成员。" />;
  }

  if (members.status === 'error') {
    return (
      <RecoverableReadError
        error={members.error}
        retryAction="retry-member-display-list"
        onRetry={() => void members.refetch()}
      />
    );
  }

  if (members.data.length === 0) {
    return <ModuleEmptyState icon="users" body="当前乐季暂无成员" />;
  }

  return <MemberDisplayRows members={members.data} />;
}

function MemberDisplayRows({ members }: { readonly members: readonly MemberDisplayListItem[] }): ReactNode {
  return (
    <section className="member-list prototype-list" role="list">
      {members.map((member) => (
        <article className="member-row prototype-list-row" role="listitem" key={member.displayMemberId}>
          <span className="member-avatar member-avatar-icon" aria-hidden="true">
            <SvgIcon name="users" className="member-avatar-svg" />
          </span>
          <span className="member-copy">
            <strong>{member.nickname}</strong>
            <small>{formatMemberGroupNames(member.memberGroupNames)}</small>
          </span>
        </article>
      ))}
    </section>
  );
}
